import React, { useMemo, useEffect, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { toast } from "react-toastify"
import Select from "react-select"
import { Button } from "reactstrap"
import { FaUsers } from "react-icons/fa"

import TableContainer from "../../../components/Common/DataTableContainer"
import StatsCard from "components/StatsCard"
import {
  fetchBazaarList,
  getBazaarEnquiryList,
  downloadBazaarEnquiryExcel,
  resendBazaarEnquiryWhatsapp,
} from "../../../redux/actions/pvs"
import {
  RESEND_BAZAAR_ENQUIRY_WHATSAPP_RESET,
  DOWNLOAD_BAZAAR_ENQUIRY_EXCEL_RESET,
} from "../../../redux/types/pvs"

const statusBadge = status => {
  if (status === "SENT") {
    return <span className="badge bg-success">Sent</span>
  }
  if (status === "FAILED") {
    return <span className="badge bg-danger">Failed</span>
  }
  if (status === "SKIPPED") {
    return <span className="badge bg-secondary">Skipped</span>
  }
  return <span className="text-muted">—</span>
}

const BazaarEnquiryList = () => {
  document.title = "Bazaar Walk-Ins | PULA"

  const dispatch = useDispatch()

  const enquiryListScreen = useSelector(
    state => state.screens.pvs.getBazaarEnquiryList
  )
  const resendScreen = useSelector(
    state => state.screens.pvs.resendBazaarEnquiryWhatsapp
  )
  const downloadScreen = useSelector(
    state => state.screens.pvs.downloadBazaarEnquiryExcel
  )
  const token = useSelector(state => state.auth.token)
  const bazaarList = useSelector(state => state.pvs.bazaarList) || []

  const [resendingId, setResendingId] = useState(null)
  const [selectedBazaar, setSelectedBazaar] = useState(null)

  const bazaarOptions = useMemo(
    () =>
      [...bazaarList]
        .sort((a, b) => Number(b.bazaar_id) - Number(a.bazaar_id))
        .map(b => ({ label: b.bazaar_name, value: b.enc_bazaar_id })),
    [bazaarList]
  )

  const fetchEnquiries = () =>
    dispatch(
      getBazaarEnquiryList({
        enc_bazaar_id: selectedBazaar ? selectedBazaar.value : undefined,
      })
    )

  useEffect(() => {
    dispatch(fetchBazaarList({ offset: 1 }, token))
  }, [])

  useEffect(() => {
    fetchEnquiries()
  }, [selectedBazaar])

  useEffect(() => {
    if (downloadScreen.success && downloadScreen.fileLink) {
      const link = document.createElement("a")
      link.href = downloadScreen.fileLink
      link.download = "bazaar-walk-ins.xlsx"
      link.click()
      dispatch({ type: DOWNLOAD_BAZAAR_ENQUIRY_EXCEL_RESET })
    }
    if (downloadScreen.error) {
      toast.error(downloadScreen.error)
      dispatch({ type: DOWNLOAD_BAZAAR_ENQUIRY_EXCEL_RESET })
    }
  }, [downloadScreen])

  useEffect(() => {
    if (resendScreen.success) {
      if (resendScreen.whatsapp_status === "SENT") {
        toast.success(resendScreen.message)
      } else {
        toast.error(resendScreen.message)
      }
      setResendingId(null)
      dispatch({ type: RESEND_BAZAAR_ENQUIRY_WHATSAPP_RESET })
      fetchEnquiries()
    }
    if (resendScreen.error) {
      toast.error(resendScreen.error)
      setResendingId(null)
      dispatch({ type: RESEND_BAZAAR_ENQUIRY_WHATSAPP_RESET })
    }
  }, [resendScreen])

  const tableData = useMemo(() => {
    return (enquiryListScreen.data || []).map(row => ({
      ...row,
      id: row.bazaar_enquiry_id,
      already_in_system: row.matched_user_id ? "Yes" : "No",
    }))
  }, [enquiryListScreen.data])

  const totalPeople = useMemo(
    () =>
      tableData.reduce((sum, row) => sum + Number(row.accompanying_count || 1), 0),
    [tableData]
  )

  const columns = useMemo(
    () => [
      {
        header: "Bazaar",
        accessorKey: "bazaar_name",
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        header: "Prospect Name",
        accessorKey: "prospect_name",
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        header: "Mobile",
        accessorKey: "mobile_no",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Heard From",
        accessorKey: "heard_from",
        enableColumnFilter: true,
        enableSorting: true,
      },
      {
        header: "People Accompanying",
        accessorKey: "accompanying_count",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "Submitted By",
        accessorKey: "submitted_by_name",
        enableColumnFilter: false,
        enableSorting: true,
      },
      {
        header: "WhatsApp",
        accessorKey: "whatsapp_status",
        enableColumnFilter: false,
        enableSorting: false,
        cell: info => statusBadge(info.row.original.whatsapp_status),
      },
      {
        header: "Action",
        accessorKey: "action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: info => {
          const row = info.row.original
          if (row.whatsapp_status !== "FAILED") return null
          return (
            <button
              className="btn btn-sm btn-outline-primary"
              disabled={resendingId === row.bazaar_enquiry_id}
              onClick={e => {
                e.stopPropagation()
                setResendingId(row.bazaar_enquiry_id)
                dispatch(
                  resendBazaarEnquiryWhatsapp({
                    enc_bazaar_enquiry_id: row.enc_bazaar_enquiry_id,
                  })
                )
              }}
            >
              {resendingId === row.bazaar_enquiry_id
                ? "Resending..."
                : "Resend"}
            </button>
          )
        },
      },
    ],
    [resendingId]
  )

  return (
    <div className="page-content">
      <div className="container-fluid">
        <div className="d-flex align-items-center justify-content-end mt-3 mb-3">
          <div className="flex-grow-1">
            <h4 className="h4 mb-0 nom-title">Bazaar Walk-Ins</h4>
          </div>
          <Button
            color="primary"
            disabled={downloadScreen.loading}
            onClick={() =>
              dispatch(
                downloadBazaarEnquiryExcel({
                  enc_bazaar_id: selectedBazaar
                    ? selectedBazaar.value
                    : undefined,
                })
              )
            }
          >
            {downloadScreen.loading ? "Downloading..." : "Download Excel"}
          </Button>
        </div>

        <div className="mb-3" style={{ maxWidth: "480px" }}>
          <Select
            options={bazaarOptions}
            value={selectedBazaar}
            onChange={setSelectedBazaar}
            classNamePrefix="select"
            placeholder="All Bazaars"
            isClearable
          />
        </div>

        <div className="mb-4" style={{ maxWidth: "320px" }}>
          <StatsCard
            title="Total Walk-Ins"
            value={totalPeople}
            icon={<FaUsers />}
            bgClass="bg-primary-soft"
          >
            <div className="text-muted small mt-1">
              {tableData.length} {tableData.length === 1 ? "entry" : "entries"},
              including people accompanying
            </div>
          </StatsCard>
        </div>

        <TableContainer
          defaultPageSize={20}
          columns={columns}
          data={tableData}
          isGlobalFilter={true}
          isPagination={true}
          isCustomPageSize={true}
          SearchPlaceholder="Search records..."
          pagination="pagination"
          isAddButton={false}
          buttonClass="btn btn-primary"
          paginationWrapper="dataTables_paginate paging_simple_numbers"
          tableClass="table-nowrap dt-responsive nowrap w-100 dataTable no-footer dtr-inline"
          theadClass="table-light"
          divClassName="table-responsive mb-4 nominee-tbl-wrapper"
        />
      </div>
    </div>
  )
}

export default BazaarEnquiryList
